import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download, Plus, Pencil, Trash2, X, Search, CalendarDays, Users, ArrowLeft, RefreshCw,
} from "lucide-react";
import { LOGO } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_STYLES = {
  pending: "bg-[#C9A227]/15 text-[#e4c766] border-[#C9A227]/40",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completed: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

const emptyForm = { name: "", phone: "", email: "", date: "", time: "", party_size: 2, special_requests: "", status: "pending" };

export default function Admin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null); // reservation object or "new"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reservations`);
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    const q = query.toLowerCase();
    const matchQ = !q || [r.name, r.phone, r.email].some((f) => (f || "").toLowerCase().includes(q));
    const matchS = statusFilter === "all" || r.status === statusFilter;
    return matchQ && matchS;
  });

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (r) => { setForm({ ...emptyForm, ...r }); setEditing(r); };
  const close = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, party_size: parseInt(form.party_size, 10) || 1 };
      if (editing === "new") {
        await axios.post(`${API}/reservations`, payload);
      } else {
        await axios.put(`${API}/reservations/${editing.id}`, payload);
      }
      await load();
      close();
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (r, status) => {
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
    await axios.put(`${API}/reservations/${r.id}`, { status });
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete reservation for ${r.name}?`)) return;
    await axios.delete(`${API}/reservations/${r.id}`);
    setRows((prev) => prev.filter((x) => x.id !== r.id));
  };

  const downloadCSV = () => {
    const headers = ["Name", "Phone", "Email", "Date", "Time", "Party Size", "Status", "Special Requests", "Created"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      lines.push([r.name, r.phone, r.email, r.date, r.time, r.party_size, r.status, r.special_requests, r.created_at].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `black-rose-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stat = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div className="min-h-screen bg-[var(--br-black)] text-[var(--br-cream)]">
      {/* Header */}
      <header className="border-b border-[var(--br-gold)]/15 bg-[var(--br-panel)]">
        <div className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Black Rose Lounge" className="h-9 w-auto" />
            <div>
              <p className="font-display text-xl leading-none">Reservations</p>
              <p className="text-xs text-[var(--br-muted)] mt-1">Owner Dashboard</p>
            </div>
          </div>
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 text-sm text-[var(--br-muted)] hover:text-[var(--br-gold)] transition-colors">
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total", value: rows.length, s: "all" },
            { label: "Pending", value: stat("pending"), s: "pending" },
            { label: "Confirmed", value: stat("confirmed"), s: "confirmed" },
            { label: "Completed", value: stat("completed"), s: "completed" },
          ].map((c) => (
            <button
              key={c.label}
              onClick={() => setStatusFilter(c.s)}
              data-testid={`admin-stat-${c.s}`}
              className={`text-left rounded-2xl border p-5 transition-colors ${statusFilter === c.s ? "border-[var(--br-gold)] bg-[var(--br-gold)]/5" : "border-[var(--br-gold)]/15 hover:border-[var(--br-gold)]/40"}`}
            >
              <p className="text-3xl font-display text-[var(--br-gold)]">{c.value}</p>
              <p className="text-xs text-[var(--br-muted)] mt-1 tracking-wide">{c.label}</p>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--br-muted)]" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone or email…"
              className="field w-full rounded-xl pl-11 pr-4 py-3 text-sm"
            />
          </div>
          <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 rounded-xl border border-[var(--br-gold)]/25 px-4 py-3 text-sm text-[var(--br-cream)] hover:border-[var(--br-gold)] transition-colors">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={downloadCSV} data-testid="admin-download-csv" className="flex items-center gap-2 rounded-xl border border-[var(--br-gold)]/25 px-4 py-3 text-sm text-[var(--br-cream)] hover:border-[var(--br-gold)] transition-colors">
            <Download size={15} /> Download CSV
          </button>
          <button onClick={openNew} data-testid="admin-add-btn" className="gold-btn flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold">
            <Plus size={16} /> Add
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-[var(--br-gold)]/15">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead>
              <tr className="border-b border-[var(--br-gold)]/15 text-left text-[var(--br-muted)]">
                <th className="px-5 py-4 font-medium">Guest</th>
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">When</th>
                <th className="px-5 py-4 font-medium">Party</th>
                <th className="px-5 py-4 font-medium">Status</th>
                <th className="px-5 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-[var(--br-muted)]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-[var(--br-muted)]">No reservations found.</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--br-gold)]/8 hover:bg-white/[0.02]" data-testid={`admin-row-${r.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-medium text-[var(--br-cream)]">{r.name}</p>
                      {r.special_requests && <p className="text-xs text-[var(--br-muted)] mt-0.5 max-w-[220px] truncate">{r.special_requests}</p>}
                    </td>
                    <td className="px-5 py-4 text-[var(--br-muted)]">
                      <p>{r.phone}</p>
                      <p className="text-xs">{r.email}</p>
                    </td>
                    <td className="px-5 py-4 text-[var(--br-cream)]">
                      <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-[var(--br-gold)]" />{r.date}</span>
                      <span className="text-xs text-[var(--br-muted)]">{r.time}</span>
                    </td>
                    <td className="px-5 py-4"><span className="flex items-center gap-1.5"><Users size={13} className="text-[var(--br-gold)]" />{r.party_size}</span></td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-status-${r.id}`}
                        value={r.status}
                        onChange={(e) => setStatus(r, e.target.value)}
                        className={`field rounded-full border px-3 py-1.5 text-xs capitalize ${STATUS_STYLES[r.status] || ""}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(r)} data-testid={`admin-edit-${r.id}`} className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--br-gold)]/20 text-[var(--br-muted)] hover:text-[var(--br-gold)] hover:border-[var(--br-gold)] transition-colors"><Pencil size={14} /></button>
                        <button onClick={() => remove(r)} data-testid={`admin-delete-${r.id}`} className="grid h-8 w-8 place-items-center rounded-lg border border-red-500/20 text-red-400/70 hover:text-red-300 hover:border-red-500/50 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Edit / Add modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.25 }}
              className="glass w-full max-w-lg rounded-3xl p-7"
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl">{editing === "new" ? "Add reservation" : "Edit reservation"}</h3>
                <button onClick={close} className="text-[var(--br-muted)] hover:text-[var(--br-cream)]"><X size={20} /></button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" testid="admin-form-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} span />
                <Field label="Phone" testid="admin-form-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="Email" testid="admin-form-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <Field label="Date" testid="admin-form-date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
                <Field label="Time" testid="admin-form-time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} placeholder="8:30 PM" />
                <Field label="Party size" testid="admin-form-party" type="number" value={form.party_size} onChange={(v) => setForm({ ...form, party_size: v })} />
                <div>
                  <label className="overline block mb-2 text-[10px]">Status</label>
                  <select data-testid="admin-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="field w-full rounded-xl px-4 py-3 text-sm capitalize">
                    {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2 text-[10px]">Special requests</label>
                  <textarea data-testid="admin-form-requests" value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} rows={2} className="field w-full rounded-xl px-4 py-3 text-sm resize-none" />
                </div>
              </div>
              <button onClick={save} disabled={saving} data-testid="admin-save-btn" className="gold-btn mt-6 w-full rounded-full py-3.5 text-sm font-semibold disabled:opacity-70">
                {saving ? "Saving…" : editing === "new" ? "Add reservation" : "Save changes"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", placeholder, span, testid }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <label className="overline block mb-2 text-[10px]">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="field w-full rounded-xl px-4 py-3 text-sm"
    />
  </div>
);
